import { API_URL } from '@api/apiDictionary';
import { EventChannel, eventChannel, Task } from 'redux-saga';
import {
  take,
  call,
  put,
  fork,
  takeLatest,
  delay,
  cancel,
  all,
} from 'redux-saga/effects';
import {
  WebSocketMessage,
  WebSocketsConnectionEnum,
  WebSocketsTypesEnum,
  WS_SEND_MESSAGE_TYPE,
} from '@dynamika-shared/services/communication/src/webSockets/webSockets.type';
import {
  SendWebSocketMessageAction,
  StartWebSocketConnectionsAction,
  StopWebSocketConnectionsAction,
} from '@dynamika-shared/services/communication/src/webSockets/actions/wsBase';
import { subscribeToActions } from './helpers';
import { useEffect } from 'react';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  COLOR_LOG,
  LOG_WEB_SOCKET_MESSAGES,
} from '@dynamika-shared/utils/development';

const PING_DELAY = 20000;
const RECONNECT_DELAY = 500;

const WS_URL_MAPPING: Record<WebSocketsConnectionEnum, string> = {
  [WebSocketsConnectionEnum.WS__READER_EVENTS]: API_URL.wsNotificationsReader,
};

const socketsConnections = new Map<string, WebSocket>();

function* handleWSMessageSaga({ payload }: SendWebSocketMessageAction) {
  try {
    const { message, wsId } = payload;
    const socket = socketsConnections.get(wsId);
    if (socket?.readyState === WebSocket.OPEN) {
      yield socketsConnections.get(wsId)?.send(
        JSON.stringify({
          messageType: WS_SEND_MESSAGE_TYPE.USER_DATA,
          content: message,
        })
      );
    } else {
      console.error(`WS__SOCKET_IS_CLOSED [ ${wsId} ]`);
    }
  } catch (e) {
    console.error('WS__SEND_MESSAGE_ERROR', e);
  }
}

export function* killWebsocketTask(
  tasks: Map<string, Task>,
  connectionName: string
) {
  try {
    const existingTask = tasks.get(connectionName);
    if (existingTask) {
      yield all([cancel(existingTask), tasks.delete(connectionName)]);
    }
  } catch (e) {
    console.error('WS__KILL_TASK_ERROR', e);
  }
}
export function* killWebsocketConnection(
  socketsConnections: Map<string, WebSocket>,
  connectionName: string
) {
  try {
    const existingSocketConnection = socketsConnections.get(connectionName);
    if (existingSocketConnection) {
      existingSocketConnection.close();
      yield socketsConnections.delete(connectionName);
    }
  } catch (e) {
    console.error('WS__KILL_SOCKET_ERROR', e);
  }
}

function* wsKeepAlive(socket: WebSocket) {
  try {
    while (true) {
      yield delay(PING_DELAY);
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ messageType: WS_SEND_MESSAGE_TYPE.PING }));
      } else {
        console.error('WS__SOCKET_IS_CLOSED');
      }
    }
  } catch (e) {
    console.error('WS__KEEP_ALIVE_ERROR', e);
  }
}

function* wsReconnect(connectionName: string) {
  yield delay(RECONNECT_DELAY);

  yield put({
    type: WebSocketsTypesEnum.WS__START_WEBSOCKET_CONNECTIONS,
    payload: [connectionName],
  });
}

const createWebSocketConnection = (url: string): Promise<WebSocket> => {
  const baseUrl =
    window.location.protocol === 'https:'
      ? `wss://${window.location.host}`
      : window.location.origin;
  return new Promise((resolve, reject) => {
    const socket = new WebSocket(
      baseUrl + url,
      `dynamika7.bearer.${sessionStorage.getItem('idToken')}`
    );

    socket.onopen = () => resolve(socket);
    socket.onerror = error => {
      console.error(error);
      reject(new Error('WS__OPEN_ERROR'));
    };
  });
};

const createWebSocketChannel = (
  socket: WebSocket,
  connectionName: string
): EventChannel<WebSocketMessage> => {
  return eventChannel(emit => {
    socket.onmessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      emit({ data, error: false, close: false, connectionName });
    };

    socket.onerror = (error: Event) => {
      emit({
        data: null,
        error: true,
        message: (error as ErrorEvent).message,
        close: false,
        connectionName,
      });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    socket.onclose = (output: any) => {
      if (output?.reason) {
        emit({
          data: null,
          error: true,
          message: output.reason,
          close: true,
          connectionName,
        });
        return;
      }

      emit({ data: null, error: false, close: true, connectionName });
    };

    return () => {
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    };
  });
};

function* handleWebSocket(url: string, connectionName: string) {
  let socket: WebSocket | null = null;
  let socketChannel: EventChannel<WebSocketMessage> | null = null;

  try {
    if (socketsConnections.has(connectionName)) {
      yield call(killWebsocketConnection, socketsConnections, connectionName);
    }

    socket = yield call(createWebSocketConnection, url);

    if (!socket) {
      return;
    }

    socketsConnections.set(connectionName, socket);

    socketChannel = yield call(createWebSocketChannel, socket, connectionName);

    if (!socketChannel) {
      return;
    }

    const keepAliveTask: Task = yield fork(wsKeepAlive, socket);

    while (true) {
      const message: WebSocketMessage = yield take(socketChannel);

      if (message.error) {
        console.error('WebSocket error', message);
      }

      if (message.close) {
        socket?.close();

        socketsConnections.delete(connectionName);
        yield cancel(keepAliveTask);

        yield call(wsReconnect, connectionName);
        return;
      }

      if (message.data) {
        if (LOG_WEB_SOCKET_MESSAGES) {
          yield call(
            COLOR_LOG,
            'yellow',
            'WebSocket Message',
            message.connectionName ?? 'event',
            message.data
          );
        }

        yield put({
          type: WebSocketsTypesEnum.WS__RECEIVED_MESSAGE,
          payload: message,
          connectionName,
        });
      }
    }
  } catch (error) {
    yield put({
      type: WebSocketsTypesEnum.WS__FAILED_WEBSOCKET_CONNECTIONS,
      error: (error as Error).message,
      connectionName,
    });
  } finally {
    if (socketChannel) {
      socketChannel.close();
    }

    if (socket) {
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    }
  }
}

export function* websocketsSaga() {
  const tasks = new Map<string, Task>();

  yield takeLatest(WebSocketsTypesEnum.WS__SEND_MESSAGE, handleWSMessageSaga);

  while (true) {
    const action:
      | StartWebSocketConnectionsAction
      | StopWebSocketConnectionsAction = yield take([
      WebSocketsTypesEnum.WS__START_WEBSOCKET_CONNECTIONS,
      WebSocketsTypesEnum.WS__STOP_WEBSOCKET_CONNECTIONS,
    ]);
    const { type, payload } = action;

    if (LOG_WEB_SOCKET_MESSAGES) {
      console.log('WebSocket action', action);
    }

    for (const connectionName of payload) {
      if (tasks.has(connectionName) || socketsConnections.has(connectionName)) {
        yield call(killWebsocketTask, tasks, connectionName);
        yield call(killWebsocketConnection, socketsConnections, connectionName);
      }

      if (type === WebSocketsTypesEnum.WS__START_WEBSOCKET_CONNECTIONS) {
        const task: Task = yield fork(
          handleWebSocket,
          WS_URL_MAPPING[connectionName],
          connectionName
        );
        tasks.set(connectionName, task);
      }
    }
  }
}

type ListenerType = (
  msg: WebSocketMessage['data'],
  error?: WebSocketMessage['error']
) => void;

export const useWebSocketSagaListener = (
  listener: ListenerType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps?: any[]
) => {
  useEffect(() => {
    function handle(action: PayloadAction<WebSocketMessage>) {
      listener(action.payload.data, action.payload.error);
    }
    return subscribeToActions(WebSocketsTypesEnum.WS__RECEIVED_MESSAGE, handle);
  }, deps || []);
};
